import { Link } from "react-router-dom";
import { useSeo } from "../lib/seo";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/motion";
import { Ph } from "../components/ui-bits";
import { PRIVACY, COOKIE, UPDATED } from "../data/legal";

const Text = ({ t }) =>
  t.split(/\[\[(.+?)\]\]/).map((part, i) => (i % 2 ? <Ph key={i}>{part}</Ph> : <span key={i}>{part}</span>));

const Table = ({ head, rows }) => (
  <div className="mt-2 border-t border-ink/15" role="table">
    <div className="hidden gap-4 border-b border-ink/15 py-3 md:grid" style={{ gridTemplateColumns: `repeat(${head.length}, minmax(0, 1fr))` }} role="row">
      {head.map((h) => <span key={h} className="eyebrow" role="columnheader">{h}</span>)}
    </div>
    {rows.map((r) => (
      <div key={r[0]} className="grid gap-2 border-b border-ink/15 py-4 md:gap-4" style={{ gridTemplateColumns: `repeat(${head.length}, minmax(0, 1fr))` }} role="row">
        {r.map((c, i) => (
          <div key={i} className="col-span-full text-sm leading-relaxed md:col-span-1 md:text-[15px]" role="cell">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.16em] text-teal md:hidden">{head[i]}</span>
            <span className={i === 0 ? "font-medium" : "text-ink/80"}><Text t={c} /></span>
          </div>
        ))}
      </div>
    ))}
  </div>
);

const Block = ({ b }) => {
  if (typeof b === "string") return <p className="text-base leading-relaxed text-ink/80"><Text t={b} /></p>;
  if (b.list) return <ul className="space-y-2 pl-5 text-base leading-relaxed text-ink/80 [list-style:square] marker:text-teal">{b.list.map((x) => <li key={x}><Text t={x} /></li>)}</ul>;
  return <Table {...b.table} />;
};

export default function LegalPage({ kind }) {
  const c = kind === "privacy" ? PRIVACY : COOKIE;
  const other = kind === "privacy" ? { to: "/cookie", label: "Cookie policy" } : { to: "/privacy", label: "Privacy policy" };
  useSeo(c.title, c.desc);
  return (
    <>
      <PageHero testid={`legal-${kind}`} eyebrow="Informazioni legali" lines={[c.title]} intro={c.intro} />
      <section className="px-5 pb-28 sm:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-y-10 lg:gap-x-10">
          <aside className="col-span-12 lg:col-span-3">
            <div className="lg:sticky lg:top-28">
              <p className="text-sm text-ink/70">Ultimo aggiornamento: {UPDATED}</p>
              <Link to={other.to} className="link-u mt-3 inline-block text-sm text-teal" data-testid={`legal-link-${other.to.slice(1)}`}>{other.label} →</Link>
            </div>
          </aside>
          <div className="col-span-12 space-y-14 lg:col-span-8 lg:col-start-5">
            {c.sections.map((s, i) => (
              <Reveal key={s.h} className="border-t border-ink/15 pt-8" data-testid={`legal-section-${i + 1}`}>
                <h2 className="flex items-baseline gap-4 text-2xl md:text-3xl"><span className="font-serif text-sm text-teal">{String(i + 1).padStart(2, "0")}</span>{s.h}</h2>
                <div className="mt-6 space-y-5">{s.blocks.map((b, j) => <Block key={j} b={b} />)}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
